/******************************************************************************
 * @Author                : Hamza Damene                                      *
 * @CreatedDate           : 2025-03-19 15:00:00                               *
 * @LastEditors           : Hamza Damene                                      *
 * @LastEditDate          : 2025-03-19 15:00:00                               *
 *****************************************************************************/

/* Imports */
import { Typography, Card, Chip } from "@material-tailwind/react";
import { t } from "i18next";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { hasFilledCompInfoValue, isCompInfoDisplayed } from "../../../../../components/data/compinfos/compinfo-usage";

/**
 * Props pour le composant AdditionalInfosSection
 */
interface AdditionalInfosSectionProps {
  workorder: EasyTech.Workorder;
}

/**
 * Section des informations complémentaires du workorder
 * @param workorder - Données du workorder
 * @returns Composant React pour la section des informations complémentaires
 */
export function AdditionalInfosSection({
  workorder,
}: AdditionalInfosSectionProps) {
  /**
   * Récupère le label traduit d'un compInfo selon la langue de l'utilisateur
   * @param compInfo - CompInfo contenant les traductions
   * @returns Label traduit ou label par défaut
   */
  const getTranslatedLabel = (compInfo: EasyTech.CompInfo): string => {
    const currentLanguage = localStorage.getItem("i18nextLng") || "en";
    
    if (compInfo.translations && Array.isArray(compInfo.translations)) {
      const translation = compInfo.translations.find(
        (translation) => translation.language?.isoCode === currentLanguage
      );
      
      if (translation && translation.label) {
        return translation.label;
      }
    }
    
    // Fallback sur le label par défaut
    return compInfo.label || "";
  };

  /**
   * Récupère le label traduit d'un choix de compInfo selon la langue de l'utilisateur
   * @param choice - Choix contenant les traductions
   * @returns Label traduit ou label par défaut
   */
  const getTranslatedChoiceLabel = (choice: EasyTech.CompInfoChoice): string => {
    const currentLanguage = localStorage.getItem("i18nextLng") || "en";
    
    if (choice.translations && Array.isArray(choice.translations)) {
      const translation = choice.translations.find(
        (translation) => translation.language?.isoCode === currentLanguage
      );
      
      if (translation && translation.label) {
        return translation.label;
      }
    }
    
    // Fallback sur le label par défaut
    return choice.label || "";
  };

  /**
   * Vérifie si une valeur est booléenne et retourne le composant approprié
   * @param value - Valeur à vérifier
   * @param compInfo - Information sur le type de compInfo
   * @returns Élément React approprié
   */
  const renderValue = (value: string | undefined, compInfo?: EasyTech.CompInfo) => {
    if (compInfo && compInfo.type === "BOOLEAN") {
      const isTrue = value === "true" || (value as any) === true;
      return (
        <Chip 
          color={isTrue ? "green" : "red"} 
          size="sm" 
          icon={isTrue ? <CheckIcon className="w-4 h-4" /> : <XMarkIcon className="w-4 h-4" />}  
          value={isTrue ? t("user:yes") : t("user:no")} 
          className={`w-fit h-fit rounded-full py-1 capitalize text-sm text-white ${isTrue ? "bg-green-100 text-green-500 text-xs font-medium" : "bg-red-100 text-red-500 text-xs font-medium"} flex-shrink-0`}
        />
      );
    }
    
    // Pour les autres types, affichage normal
    return <Typography>{value || "-"}</Typography>;
  };

  return (
    <>
      <div className="information-header">
        <Typography variant="h4">{t("workorder:additionalInfos")}</Typography>
      </div>

      <Card className="information-content">
        <>
        
          {[
            ...(workorder.compInfos?.inputs || [])
              .filter((ici) => isCompInfoDisplayed(ici?.compInfo, workorder.status, hasFilledCompInfoValue(ici?.value)))
              .map((ici) => ({ kind: "input" as const, item: ici, order: ici.compInfo?.order })),
            ...(workorder.compInfos?.selects || [])
              .filter((sci) => isCompInfoDisplayed(
                sci?.compInfo,
                workorder.status,
                Array.isArray(sci?.values) && sci.values.length > 0
              ))
              .map((sci) => ({ kind: "select" as const, item: sci, order: sci.compInfo?.order }))
          ]
            .sort((a, b) => (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY))
            .map((row) => row.kind === "input" ? (
              <div className="field" key={row.item.id || `input-${row.item.compInfo?.id}`}>
                <label>{row.item.compInfo ? getTranslatedLabel(row.item.compInfo) : ""}</label>
                {renderValue(row.item.value, row.item.compInfo)}
              </div>
            ) : (
              <div className="field" key={row.item.id || `select-${row.item.compInfo?.id}`}>
                <label>{row.item.compInfo ? getTranslatedLabel(row.item.compInfo) : ""}</label>
                {row.item.values &&
                  Array.isArray(row.item.values) &&
                  row.item.values.map(
                    (c, idx) =>
                      c &&
                      c.value && (
                        <Typography key={`value-${idx}`}>
                          {getTranslatedChoiceLabel(c.value)}
                        </Typography>
                      )
                  )}
              </div>
            ))}
        </>
      </Card>
    </>
  );
}
